import type { EmailMessage, EmailStatus, EmailType } from '@vy/types';
import { EMAIL_TYPES, EMAIL_STATUSES } from '@vy/types';
import {
  groupBy,
  newUUID,
  uniqueExtract,
  validateNotNullOrUndefined,
  vyDate,
  getMapEntries,
  getMinDate,
  validateNotEmptyArray,
  first,
} from '@vy/utils';

export function getEmailActivities(emailMessages: EmailMessage[]) {
  const relevantEmails = filterRelevantEmails(emailMessages);
  const groupedEmails = groupEmailsByDateTypeStatus(relevantEmails);

  return mapGroupedEmailsToActivityEvents(groupedEmails);
}

function filterRelevantEmails(emails: EmailMessage[]) {
  return emails.filter((email) => {
    return isSupportedType(email.type) && isSupportedStatus(email.status);
  });
}

function isSupportedType(type: EmailType) {
  const supportedTypes: EmailType[] = [EMAIL_TYPES.SharedInvoice, EMAIL_TYPES.PaymentReminder];

  return supportedTypes.includes(type);
}

function isSupportedStatus(status: EmailStatus) {
  const supportedStatuses: EmailStatus[] = [EMAIL_STATUSES.Delivered, EMAIL_STATUSES.Failed];

  return supportedStatuses.includes(status);
}

function groupEmailsByDateTypeStatus(emails: EmailMessage[]) {
  return groupBy(emails, (email) => {
    const day = vyDate(email.date).startOf('day').toISOString();

    return `${day}-${email.type}-${email.status}`;
  });
}

function mapGroupedEmailsToActivityEvents(groupedEmails: Map<string, EmailMessage[]>) {
  return getMapEntries(groupedEmails).map(([_, emails]) => {
    return createActivityEvent(emails);
  });
}

function createActivityEvent(emails: EmailMessage[]) {
  const recipients = getRecipients(emails);
  const firstEmail = first(emails);
  const label = getActivityLabel(firstEmail);
  const dates = emails.map((email) => new Date(email.date));

  validateNotNullOrUndefined(firstEmail, 'firstEmail');
  validateNotEmptyArray(dates, 'dates');
  validateNotNullOrUndefined(label, 'label');

  return {
    id: newUUID(),
    label,
    email: recipients,
    date: getMinDate(...dates),
    success: isDelivered(firstEmail),
  };
}

function getRecipients(emails: EmailMessage[]) {
  const recipients = uniqueExtract(emails, 'recipient');

  return recipients.join(', ');
}

function getActivityLabel(email?: EmailMessage) {
  validateNotNullOrUndefined(email, 'email');

  if (isDelivered(email)) {
    return getDeliveredLabel(email);
  }

  if (isFailed(email)) {
    return getFailedLabel(email);
  }

  return undefined;
}

function getDeliveredLabel(email: EmailMessage) {
  if (isSharedInvoice(email)) {
    return 'Delivered (Email sent)';
  }

  if (isPaymentReminder(email)) {
    return 'Delivered (Invoice reminder)';
  }

  return undefined;
}

function getFailedLabel(email: EmailMessage) {
  if (isSharedInvoice(email)) {
    return 'Email delivery failed (Invoice)';
  }

  if (isPaymentReminder(email)) {
    return 'Email delivery failed (Invoice reminder)';
  }

  return undefined;
}

function isDelivered(email: EmailMessage) {
  return email.status === EMAIL_STATUSES.Delivered;
}

function isFailed(email: EmailMessage) {
  return email.status === EMAIL_STATUSES.Failed;
}

function isSharedInvoice(email: EmailMessage) {
  return email.type === EMAIL_TYPES.SharedInvoice;
}

function isPaymentReminder(email: EmailMessage) {
  return email.type === EMAIL_TYPES.PaymentReminder;
}
